/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  DUTY EXECUTION — THE VOCABULARY. [P5c]
 *
 *  PA_REQUIREMENTS §5.4 and Appendix B. An officer marks Start Duty, Mark Reached and
 *  Stand Down; a commander may mark on behalf of somebody whose phone is dead; and an
 *  officer with no Reached by the end of the deployment is absent on spot.
 *
 *  ⚠️ THESE ARE `constant.ts` AND NOT `schema/`, AND THAT IS STRUCTURAL RATHER THAN TIDY.
 *  `pwa/` reads all three tuples at RUNTIME — the offline queue stores a `type`, the
 *  roster renders a state, and `i18n.test.ts` iterates the states to prove the catalogue
 *  covers every one. Every module in `schema/` imports zod, and `pwa/` has a 150 KB
 *  budget with a gate that reads the BUILT bundle. Putting them there would not be a
 *  style error; it would make them unusable by the app that needs them, which is exactly
 *  why [P5b] moved the officer-surface bounds out of `schema/officerApp.schema.ts`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * What an officer (or their commander) recorded.
 *
 * ⚠️ `ABSENT` IS AN OVERRIDE, NOT THE ONLY ROUTE TO ABSENCE. FR-5.4.6 DERIVES absence at
 * the end of a deployment where no `REACHED` exists, and that derivation needs no row at
 * all. This member is FR-5.4.6's explicit twin — a commander stating a fact before the
 * duty ends — and where both are available the explicit one wins.
 */
export const DUTY_EVENT_TYPES = ['START_DUTY', 'REACHED', 'STAND_DOWN', 'ABSENT'] as const;
export type DutyEventType = (typeof DUTY_EVENT_TYPES)[number];

/**
 * Why a marking carries no coordinates — FR-5.4.4 asks for them on every action, and
 * OD-11 settles that they are captured and never validated.
 *
 * ⚠️ THE ABSENCE OF A FIX IS RECORDED, NEVER INFERRED FROM A NULL. "The officer declined"
 * and "the phone could not see the sky" are different facts, and an officer who declined
 * the prompt once must still be able to mark duty for ever (§4c). A single nullable
 * coordinate pair cannot say which happened; this can.
 *
 * `UNAVAILABLE` covers the case no test can see: `navigator.geolocation` is
 * secure-context-only, so over plain `http://` to anything but `localhost` it is simply
 * absent. See CLAUDE.md §8's secure-context ban — Node and jsdom expose it
 * unconditionally, so a green suite says nothing about it.
 */
export const GEO_STATUSES = ['CAPTURED', 'DENIED', 'TIMEOUT', 'UNAVAILABLE'] as const;
export type GeoStatus = (typeof GEO_STATUSES)[number];

/**
 * The largest accuracy radius, in metres, a handset fix may report on the wire — the marking's
 * geo block and the location send (`shareLocationBodySchema`) both refuse anything wider.
 *
 * ⚠️ [ANALYTICS §7] A CONSTANT RATHER THAN A LITERAL IN TWO SCHEMAS, because `pwa/` must read it
 * at RUNTIME: a location send whose fix is coarser than this is refused with a 422, so the app
 * checks first rather than spending a request on every open to be told the same thing.
 */
export const GEO_ACCURACY_MAX_METRES = 100_000;

/**
 * PA_REQUIREMENTS Appendix B, verbatim: `NOT_STARTED → STARTED → REACHED → STOOD_DOWN`,
 * with `ABSENT` at deployment end and `EXEMPT` for command.
 *
 * ⚠️ DERIVED ON EVERY READ AND STORED NOWHERE. A stored attendance column would be a
 * second answer to a question the events already answer, and the two disagree in exactly
 * the case P5c exists for — an offline batch landing out of order.
 *
 * ⚠️ `EXEMPT` IS WHAT MAKES ONE FIELD CORRECT FOR BOTH ROSTERS. FR-5.4.7 exempts command
 * from absence flagging, and [P2]'s per-policy command roles generalise that from
 * "Incharge and Inspector" to all of them. Because `appCommandOfficerSchema` EXTENDS the
 * force one, a command row needs a value for this field, and `EXEMPT` is the true one.
 * It replaces only the DERIVED absence: a commander who does start duty reads `STARTED`.
 *
 * ⚠️ [C5c §5a] SIX STATES ON THE WIRE, THREE ON A SCREEN. What an officer and a supervisor READ is
 * `Not arrived · Present · Absent`, and `presenceOf` (`presence.constant.ts`) is the one reduction
 * from these six to those three. The six stay: the ladder, the roster and the dashboard's own tile
 * all decide from them, and a display rule is no reason to throw a fact away.
 */
export const ATTENDANCE_STATES = ['NOT_STARTED', 'STARTED', 'REACHED', 'STOOD_DOWN', 'ABSENT', 'EXEMPT'] as const;
export type AttendanceState = (typeof ATTENDANCE_STATES)[number];

/**
 * ⚠️ [C5c §5c] WHY A COMMANDER MARKED SOMEBODY ABSENT — THE CODES DUTY MITRA SENDS.
 *
 * ⚠️ THE WIRE STAYS FREE TEXT, AND THIS IS THE DOCUMENTED SET RATHER THAN AN ENUM. `reason` on the
 * duty event is `z.string()` and remains so: the native `app/` team may already send prose, and a
 * closed enum would 422 their absences. What changed is that the PWA sends a CODE rather than its
 * own translated label — English stored in a fact is a word nobody can translate later, and the
 * audit row carries the reason only when it is one of these (`dutyEvent.service.ts`).
 */
export const ABSENCE_REASONS = ['NO_SHOW', 'OTHER_DUTY', 'LEAVE'] as const;
export type AbsenceReason = (typeof ABSENCE_REASONS)[number];

/**
 * ⚠️ [C1c §2d] HOW STRONG THE EVIDENCE IS THAT THE **SUBJECT** WAS THERE.
 *
 * A SECOND AXIS, NEVER A SIXTH `ATTENDANCE_STATE`. [LIFECYCLE]'s rule: a status member would
 * force every existing attendance query to be re-read, where a second column leaves them all
 * working — which is the whole argument. A commander-attested `REACHED` is still `REACHED`,
 * because the officer IS present; what differs is who says so.
 *
 * - `SELF`      — the subject tapped it, on their own device. The strongest claim available.
 * - `ON_BEHALF` — a commander tapped it for them, so the position and the clock are the
 *                 COMMANDER's. That is fine evidence about attendance and no evidence at all
 *                 about where a place is; `dutyEvent.service.ts`'s enrichment gate says so.
 * - `INFERRED`  — the server wrote it as a consequence of another marking (§2b's coupling).
 *                 Nobody tapped this rung.
 *
 * ⚠️ THE MEMBER IS `ON_BEHALF` AND MUST NEVER BE `COMMAND`. `commandRoleIsolation.test.ts`
 * CLAIM 5 scans for `\bcommands\b` and `role_key`, and a member named `COMMAND` would put
 * `attendance.service.ts` and every renderer onto its exact array for a string that counts
 * nothing. It is also the more precise word: an ad-hoc supervisor holds no command ROLE.
 *
 * ⚠️ AND IT IS DERIVED ON READ FROM COLUMNS THAT ALREADY EXIST — `actor_officer_id` versus
 * `subject_officer_id`, plus `derived_from_event_id`. There is no `geo_source` column and
 * none may be added: whose fix it is IS the actor/subject comparison, exactly derivable from
 * two `required` columns on the same row, which is the SKEW side of `dutyEvent.model.ts`'s
 * own test rather than the `clock_implausible` side.
 */
export const DUTY_EVENT_ATTESTATIONS = ['SELF', 'ON_BEHALF', 'INFERRED'] as const;
export type DutyEventAttestation = (typeof DUTY_EVENT_ATTESTATIONS)[number];

/**
 * ⚠️ [C1c §2b] THE DERIVED-EVENT NAMESPACE, AND WHY IT IS TWO CHARACTERS.
 *
 * One tap records two rows (see `DUTY_EVENT_ATTESTATIONS`), and the second needs its own
 * `client_event_id` because that column carries a UNIQUE index. It is minted deterministically
 * from the tapped id — `~d<tapped>` — so a REPLAY of the tapped marking maps to the same
 * derived id rather than to a second row.
 *
 * ⚠️ THE PREFIX IS OUTSIDE THE WIRE'S CHARSET, AND THAT IS THE WHOLE MECHANISM.
 * `dutyEventInputSchema` admits `[A-Za-z0-9_-]` only, so no client can mint into this
 * namespace and no derived row can ever collide with a tapped one. The guard is the regex
 * that already exists rather than a new rule somebody has to remember.
 *
 * ⚠️ AND A CLIENT MUST NEVER RECONCILE ITS QUEUE AGAINST A DERIVED ID. It is not in the
 * queue, was never sent, and `queueRules.ts` keys on `clientEventId`.
 */
export const DERIVED_CLIENT_EVENT_ID_PREFIX = '~d';

/** What a CLIENT may send. The wire bound, unchanged since P5c. */
export const CLIENT_EVENT_ID_MAX_LENGTH = 64;

/** What the COLUMN must hold: the wire bound plus the derived prefix. */
export const DERIVED_CLIENT_EVENT_ID_MAX_LENGTH = CLIENT_EVENT_ID_MAX_LENGTH + DERIVED_CLIENT_EVENT_ID_PREFIX.length;

/**
 * How many queued markings one `POST /app/duty-events/sync` may carry.
 *
 * An officer coming out of a basement has a shift's worth, not a month's: 4 duties × a
 * start, a stand-down and a handful of stops is comfortably inside this. It bounds the
 * body and the per-item transaction count, and the client drains in successive batches
 * rather than being refused — so the number costs nothing when it is exceeded.
 */
export const APP_SYNC_MAX_EVENTS = 100;

/**
 * ⚠️ THE UNION LIST'S BOUND, AND IT THROWS RATHER THAN NARROWING.
 *
 * "My duties" is now two sources — the assignments an officer holds and the duties they
 * COMMAND — merged, de-duplicated and paged. Merging requires reading the key tuples for
 * the whole window from both sides, so the window needs a bound that is not the page
 * size. (`basePaginationSchema.page` has no maximum, so skip/limit per side would need
 * `skip + limit` rows from each before the merge, which is unbounded.)
 *
 * THE ARITHMETIC, because a bound without one is a number somebody lowers: the widest
 * window is `APP_DUTIES_MAX_WINDOW_DAYS` (180) and one duty point may carry
 * `ARRANGEMENT_LIMITS.SHIFTS` (6) slots in a day, so an officer on every shift of every
 * day for six months is 1 080 rows. 1 200 clears that with headroom. The rows are
 * projections of three columns, so this is tens of kilobytes, not megabytes.
 *
 * ⚠️ IT THROWS, exactly as `APP_DUTIES_MAX_WINDOW_DAYS` and `INBOX_MAX_WINDOW_DAYS` do,
 * and for their reason: a silently truncated list under-reports duties, and an officer
 * who is not shown a duty does not turn up. The refusal names its way through — narrow
 * the date range.
 *
 * ⚠️ AND THE NUMBER IS NOT IMPORTED FROM `ARRANGEMENT_LIMITS`, deliberately. That lives
 * in `schema/arrangement.schema.ts`, which imports zod; importing it here would drag the
 * whole validation library into `pwa/`'s budget for one integer. The arithmetic is
 * written out above instead, which is what the reader needs anyway.
 */
export const APP_DUTIES_MAX_WINDOW_ROWS = 1_200;
